import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, ToggleLeft, ToggleRight, GraduationCap, Lock, Info, TrendUp, ArrowsCounterClockwise, LinkSimple, Star } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const PROGRAM_OPTIONS = [
  { value: "petits_samourais",   label: "Petits Samouraïs" },
  { value: "pco",                label: "PCV (Programme Ceinture Verte)" },
  { value: "pcn",                label: "PCN (Programme Cours Niveau)" },
  { value: "master_club",        label: "Master Club" },
  { value: "parents_enfants",    label: "Parents-Enfants" },
  { value: "prive",              label: "Cours Privés" },
  { value: "compétitif_muaythai_24", label: "Programme Compétitif Muay Thaï (24 sem)" },
  { value: "famille_rabais",     label: "Rabais Famille" },
  { value: "autre",              label: "Autre" },
];

const CYCLE_OPTIONS = [
  { value: "mensuel",      label: "Mensuel" },
  { value: "session",      label: "Session" },
  { value: "trimestriel",  label: "Trimestriel" },
  { value: "annuel",       label: "Annuel" },
  { value: "horaire",      label: "À l'heure" },
  { value: "ponctuel",     label: "Ponctuel" },
];

const EMPTY_FORM = {
  program_id: "pco", label: "", age_range: "", sessions_per_week: "",
  price: "", billing_cycle: "mensuel", duration_weeks: "",
  family_discount_pct: "", notes: "", active: true, sort_order: 0,
};

export default function DojoTariffsModule() {
  const [tariffs, setTariffs] = useState([]);
  const [editing, setEditing] = useState(null);  // id en cours d'édition ou "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [showIncrease, setShowIncrease] = useState(false);

  const load = () => api.get("/dojo-tariffs").then(r => setTariffs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // ── Synchronisation vers Stripe ──
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const pollSync = async () => {
    try {
      const { data } = await api.get("/dojo-tariffs/sync-stripe/status");
      setSyncStatus(data);
      if (data.running) { setTimeout(pollSync, 3000); }
      else { setSyncing(false); if (data.state === "done") { toast.success(`Synchro terminée · ${data.created || 0} créés, ${data.updated || 0} mis à jour, ${data.archived || 0} kaizen archivés`); load(); } }
    } catch { setSyncing(false); }
  };
  useEffect(() => { pollSync(); }, []); // eslint-disable-line
  const syncStripe = async () => {
    try {
      const { data } = await api.post("/dojo-tariffs/sync-stripe");
      const msg = `Envoyer ${data.total} tarifs vers Stripe ?\n\n• ${data.skipped} exclus (0 $ et passages de ceinture)\n• Les cours "session" deviennent des abonnements aux 4 semaines\n• Taxe (TPS/TVQ) ajoutée en sus au checkout\n• Les anciens forfaits Kaizen seront archivés\n\nÀ lancer depuis la PRODUCTION (clé sk_live_).`;
      if (!window.confirm(msg)) return;
      setSyncing(true);
      const r = await api.post("/dojo-tariffs/sync-stripe?confirm=true");
      if (r.data.already_running) { toast.info("Une synchro est déjà en cours."); }
      else { toast.success(`Synchro lancée pour ${r.data.total} tarifs 🚀`); }
      setTimeout(pollSync, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de synchro Stripe."); setSyncing(false); }
  };

  const startNew = () => { setForm(EMPTY_FORM); setEditing("new"); };
  const startEdit = (t) => {
    setForm({
      ...EMPTY_FORM, ...t,
      sessions_per_week: t.sessions_per_week ?? "",
      duration_weeks: t.duration_weeks ?? "",
      family_discount_pct: t.family_discount_pct ?? "",
      age_range: t.age_range ?? "",
      notes: t.notes ?? "",
    });
    setEditing(t.id);
  };

  const cancel = () => { setEditing(null); setForm(EMPTY_FORM); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || form.price === "" || isNaN(Number(form.price))) {
      toast.error("Libellé et prix obligatoires"); return;
    }
    setBusy(true);
    try {
      const payload = {
        program_id: form.program_id,
        label: form.label.trim(),
        age_range: form.age_range || null,
        sessions_per_week: form.sessions_per_week === "" ? null : Number(form.sessions_per_week),
        price: Number(form.price),
        billing_cycle: form.billing_cycle,
        duration_weeks: form.duration_weeks === "" ? null : Number(form.duration_weeks),
        family_discount_pct: form.family_discount_pct === "" ? null : Number(form.family_discount_pct),
        notes: form.notes,
        active: form.active,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editing === "new") {
        await api.post("/dojo-tariffs", payload);
        toast.success("Tarif ajouté");
      } else {
        await api.patch(`/dojo-tariffs/${editing}`, payload);
        toast.success("Tarif mis à jour");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const toggleActive = async (t) => {
    await api.patch(`/dojo-tariffs/${t.id}`, { active: !t.active });
    load();
  };
  const toggleRecommended = async (t) => {
    await api.patch(`/dojo-tariffs/${t.id}`, { recommended: !t.recommended });
    toast.success(!t.recommended ? "Ajouté aux recommandés publics ⭐" : "Retiré des recommandés");
    load();
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer "${t.label}" ?`)) return;
    await api.delete(`/dojo-tariffs/${t.id}`);
    toast.success("Supprimé"); load();
  };

  const fmtPrice = (p, cycle) => `${p.toFixed(2)} $ ${cycle === "horaire" ? "/ h" : `/ ${cycle}`}`;

  return (
    <div data-testid="dojo-tariffs-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Tarifs Dojo"
          breadcrumbs={[{ label: "Dojo & élèves" }, { label: "Tarifs Dojo" }]}
          subtitle={<>Grille tarifaire école / programmes. Module strictement interne — jamais affiché sur le mini-site public <code className="font-mono text-xs bg-zinc-800 px-1.5 rounded">/circuit</code>.</>}
          actions={
            <>
              <button onClick={startNew} disabled={editing !== null} className="bg-[#D90429] hover:bg-[#BF0424] disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 transition-colors" data-testid="new-tariff-btn">
                <Plus size={14}/> Nouveau tarif
              </button>
              <button onClick={() => setShowIncrease(true)} className="bg-foreground hover:bg-impact-gold hover:text-zinc-100 text-white px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 transition-colors" data-testid="annual-increase-btn">
                <TrendUp size={14}/> Augmentation annuelle
              </button>
              <button onClick={syncStripe} disabled={syncing} className="bg-[#635BFF] hover:bg-[#514dcc] disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 transition-colors" data-testid="sync-stripe-btn">
                <ArrowsCounterClockwise size={14} className={syncing ? "animate-spin" : ""}/> {syncing ? "Synchro en cours…" : "Synchroniser vers Stripe"}
              </button>
            </>
          }
        />
      </div>

      <div className="px-8 lg:px-12 py-8 space-y-6">
        <div className="border border-zinc-800 p-4 bg-zinc-700/30 flex items-start gap-3 text-sm">
          <Lock size={18} className="text-[#D90429] mt-0.5 shrink-0"/>
          <div>
            <strong>Cloisonnement strict.</strong> Cette grille ne s'affiche QUE dans cette page admin. Elle est techniquement isolée des futurs tarifs Circuit (frais inscription compétition) qui seront sur une autre collection MongoDB et un autre endpoint.
          </div>
        </div>

        {syncStatus && (syncStatus.running || syncStatus.state === "done") && (
          <div className="border border-zinc-800 p-4 bg-[#635BFF]/10 border-[#635BFF]/40" data-testid="sync-status-banner">
            {syncStatus.running ? (
              <div>
                <div className="flex items-center gap-2 text-[#635BFF] font-semibold text-sm">
                  <ArrowsCounterClockwise size={16} className="animate-spin"/> Synchronisation Stripe en cours… {syncStatus.done || 0}/{syncStatus.total || 0}
                </div>
                <div className="mt-2 h-2 bg-zinc-700 rounded-full overflow-hidden">
                  <div className="h-full bg-[#635BFF] transition-all" style={{ width: `${syncStatus.total ? Math.round(((syncStatus.done || 0) / syncStatus.total) * 100) : 0}%` }} />
                </div>
              </div>
            ) : (
              <div className="text-sm text-zinc-100">
                ✅ Dernière synchro Stripe : <strong>{syncStatus.created || 0}</strong> créés · <strong>{syncStatus.updated || 0}</strong> mis à jour · <strong>{syncStatus.archived || 0}</strong> forfaits Kaizen archivés
                {syncStatus.errors && syncStatus.errors.length > 0 && <span className="text-impact-red"> · {syncStatus.errors.length} erreur(s)</span>}
              </div>
            )}
          </div>
        )}

        {editing && (
          <form onSubmit={submit} className="border border-zinc-800 p-6 space-y-4 bg-zinc-900" data-testid="tariff-form">
            <div className="ticker text-zinc-400">{editing === "new" ? "Nouveau tarif" : "Modifier le tarif"}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="ticker text-zinc-400 block mb-1">Programme</label>
                <select value={form.program_id} onChange={(e) => setForm({...form, program_id: e.target.value})} className="w-full border border-zinc-800 px-3 py-2 bg-zinc-800" data-testid="form-program">
                  {PROGRAM_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="ticker text-zinc-400 block mb-1">Cycle de facturation</label>
                <select value={form.billing_cycle} onChange={(e) => setForm({...form, billing_cycle: e.target.value})} className="w-full border border-zinc-800 px-3 py-2 bg-zinc-800" data-testid="form-cycle">
                  {CYCLE_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="ticker text-zinc-400 block mb-1">Libellé (visible dans la liste)</label>
                <input required value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} placeholder="Ex: PCV 2 cours/semaine — adultes" className="w-full border border-zinc-800 px-3 py-2" data-testid="form-label"/>
              </div>
              <div>
                <label className="ticker text-zinc-400 block mb-1">Prix (avant taxes)</label>
                <input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} placeholder="0.00" className="w-full border border-zinc-800 px-3 py-2 font-mono" data-testid="form-price"/>
              </div>
              <div>
                <label className="ticker text-zinc-400 block mb-1">Tranche d'âge (optionnel)</label>
                <input value={form.age_range} onChange={(e) => setForm({...form, age_range: e.target.value})} placeholder="Ex: 4-6 ans, 7-12 ans, 13+" className="w-full border border-zinc-800 px-3 py-2"/>
              </div>
              <div>
                <label className="ticker text-zinc-400 block mb-1">Cours / semaine</label>
                <input type="number" min="0" value={form.sessions_per_week} onChange={(e) => setForm({...form, sessions_per_week: e.target.value})} placeholder="—" className="w-full border border-zinc-800 px-3 py-2"/>
              </div>
              <div>
                <label className="ticker text-zinc-400 block mb-1">Durée (semaines)</label>
                <input type="number" min="0" value={form.duration_weeks} onChange={(e) => setForm({...form, duration_weeks: e.target.value})} placeholder="Ex: 24" className="w-full border border-zinc-800 px-3 py-2"/>
              </div>
              <div>
                <label className="ticker text-zinc-400 block mb-1">Rabais famille (%)</label>
                <input type="number" min="0" max="100" step="1" value={form.family_discount_pct} onChange={(e) => setForm({...form, family_discount_pct: e.target.value})} placeholder="Ex: 10 ou 15" className="w-full border border-zinc-800 px-3 py-2"/>
              </div>
              <div>
                <label className="ticker text-zinc-400 block mb-1">Ordre de tri</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: e.target.value})} className="w-full border border-zinc-800 px-3 py-2"/>
              </div>
              <div className="md:col-span-2">
                <label className="ticker text-zinc-400 block mb-1">Notes internes (optionnel)</label>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full border border-zinc-800 px-3 py-2"/>
              </div>
              <label className="flex items-center gap-2 ticker text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})}/> Actif
              </label>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={busy} className="bg-[#D90429] hover:bg-[#BF0424] disabled:opacity-50 text-white px-5 py-2.5 ticker transition-colors" data-testid="form-submit">
                {busy ? "Enregistrement…" : editing === "new" ? "Ajouter" : "Mettre à jour"}
              </button>
              <button type="button" onClick={cancel} className="border border-zinc-800 px-5 py-2.5 ticker hover:bg-zinc-700 hover:text-white transition-colors">Annuler</button>
            </div>
          </form>
        )}

        {tariffs.length === 0 && !editing && (
          <div className="border border-zinc-800 p-12 text-center" data-testid="empty-state">
            <GraduationCap size={48} className="text-zinc-400 mx-auto mb-4"/>
            <h3 className="text-zinc-100 font-display font-bold text-xl mb-2">Aucun tarif enregistré</h3>
            <p className="text-sm text-zinc-400 mb-6 max-w-md mx-auto">Commence par ajouter ta grille tarifaire 2026 (PCV, PCN, Master Club, Programme Compétitif Muay Thaï 24 sem, etc.).</p>
            <button onClick={startNew} className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker">
              <Plus size={14} className="inline mr-2"/> Ajouter le premier tarif
            </button>
          </div>
        )}

        {tariffs.length > 0 && (
          <div className="border border-zinc-800" data-testid="tariffs-list">
            {tariffs.map(t => {
              const prog = PROGRAM_OPTIONS.find(p => p.value === t.program_id);
              return (
                <div key={t.id} className={`p-5 border-b border-zinc-800 last:border-0 flex items-start justify-between gap-4 ${!t.active ? "opacity-50" : ""}`} data-testid={`tariff-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="ticker text-zinc-400">{prog?.label || t.program_id}</span>
                      {!t.active && <span className="ticker bg-zinc-700 px-2 py-0.5">inactif</span>}
                      {t.duration_weeks && <span className="ticker bg-[#E9C46A]/30 px-2 py-0.5">{t.duration_weeks} sem</span>}
                      {t.family_discount_pct && <span className="ticker bg-[#2A9D8F] text-white px-2 py-0.5">−{t.family_discount_pct}% famille</span>}
                      {t.stripe_price_id && <span className="ticker bg-[#635BFF] text-white px-2 py-0.5" title={t.stripe_mode === "subscription" ? "Abonnement Stripe" : "Paiement unique Stripe"}>Stripe ✓</span>}
                    </div>
                    <div className="text-zinc-100 font-display font-bold text-lg">{t.label}</div>
                    <div className="text-xs text-zinc-400 mt-1 flex flex-wrap gap-3">
                      {t.age_range && <span>{t.age_range}</span>}
                      {t.sessions_per_week && <span>{t.sessions_per_week} cours/sem</span>}
                      {t.notes && <span className="italic">{t.notes}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-zinc-100 font-mono text-lg font-bold">{fmtPrice(t.price, t.billing_cycle)}</div>
                    {t.stripe_payment_url && (
                      <button onClick={() => { navigator.clipboard.writeText(t.stripe_payment_url); toast.success("Lien de paiement copié !"); }}
                        title="Copier le lien de paiement Stripe" className="hover:text-[#635BFF]" data-testid={`copy-paylink-${t.id}`}>
                        <LinkSimple size={18}/>
                      </button>
                    )}
                    <button onClick={() => toggleRecommended(t)} title={t.recommended ? "Recommandé public (cliquer pour retirer)" : "Marquer recommandé (public)"} className={t.recommended ? "text-impact-gold" : "text-zinc-500 hover:text-impact-gold"} data-testid={`reco-toggle-${t.id}`}>
                      <Star size={18} weight={t.recommended ? "fill" : "regular"}/>
                    </button>
                    <button onClick={() => toggleActive(t)} title={t.active ? "Désactiver" : "Activer"} className="hover:text-[#D90429]">
                      {t.active ? <ToggleRight size={22}/> : <ToggleLeft size={22}/>}
                    </button>
                    <button onClick={() => startEdit(t)} className="hover:text-[#D90429]" title="Modifier"><PencilSimple size={18}/></button>
                    <button onClick={() => remove(t)} className="hover:text-[#D90429]" title="Supprimer"><Trash size={18}/></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showIncrease && <AnnualIncreasePanel onClose={() => { setShowIncrease(false); load(); }} />}
    </div>
  );
}

// =================== ANNUAL INCREASE PANEL ===================
function AnnualIncreasePanel({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, p, h] = await Promise.all([
        api.get("/dojo-tariffs/annual-increase/config"),
        api.get("/dojo-tariffs/annual-increase/preview"),
        api.get("/dojo-tariffs/annual-increase/history"),
      ]);
      setCfg(c.data);
      setPreview(p.data);
      setHistory(h.data);
    } catch (e) {
      toast.error("Erreur chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const saveCfg = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    try {
      await api.put("/dojo-tariffs/annual-increase/config", patch);
      const p = await api.get("/dojo-tariffs/annual-increase/preview");
      setPreview(p.data);
      toast.success("Config sauvegardée");
    } catch { toast.error("Erreur sauvegarde"); }
  };

  const applyNow = async () => {
    if (!window.confirm(`⚠️ Confirmer l'augmentation de ${cfg.pct}% MAINTENANT ? Cette action est journalisée et réversible via l'historique.`)) return;
    setApplying(true);
    try {
      const r = await api.post("/dojo-tariffs/annual-increase/apply?confirm=true");
      toast.success(`✅ ${r.data.tariffs_changed} tarifs augmentés, ${r.data.contracts_updated} contrats ajustés`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setApplying(false); }
  };

  const rollback = async (h) => {
    if (!window.confirm(`Annuler l'augmentation appliquée le ${new Date(h.applied_at).toLocaleDateString("fr-CA")} ? Les anciens prix seront restaurés.`)) return;
    try {
      const r = await api.post(`/dojo-tariffs/annual-increase/rollback/${h.id}`);
      toast.success(`${r.data.restored} tarifs restaurés`);
      loadAll();
    } catch { toast.error("Erreur rollback"); }
  };

  if (loading || !cfg) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-zinc-800 p-8 border border-zinc-800">Chargement…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="annual-increase-modal">
      <div className="bg-zinc-800 border border-zinc-800 max-w-5xl w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="ticker text-impact-gold text-xs flex items-center gap-2"><TrendUp size={14}/> AUGMENTATION ANNUELLE COÛT DE LA VIE</div>
            <h2 className="text-zinc-100 font-display font-bold text-2xl mt-1">Configuration automatique</h2>
            <p className="text-sm text-zinc-400">Augmente tous tes tarifs chaque année selon ton taux + arrondi configurés. Email automatique aux membres (préavis Loi 25).</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-impact-red text-2xl">×</button>
        </div>

        {/* Config */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 border border-zinc-800 p-4 bg-zinc-700/30 mb-4">
          <label className="flex flex-col gap-1">
            <span className="ticker text-xs text-zinc-400">Actif</span>
            <select value={cfg.active ? "y" : "n"} onChange={(e) => saveCfg({ active: e.target.value === "y" })} className="border border-zinc-800 px-2 py-1.5" data-testid="cfg-active">
              <option value="y">✅ Oui</option>
              <option value="n">❌ Non</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="ticker text-xs text-zinc-400">% augmentation</span>
            <input type="number" step="0.1" value={cfg.pct} onChange={(e) => saveCfg({ pct: parseFloat(e.target.value) || 0 })} className="border border-zinc-800 px-2 py-1.5" data-testid="cfg-pct"/>
          </label>
          <label className="flex flex-col gap-1">
            <span className="ticker text-xs text-zinc-400">Mois</span>
            <select value={cfg.month} onChange={(e) => saveCfg({ month: parseInt(e.target.value) })} className="border border-zinc-800 px-2 py-1.5" data-testid="cfg-month">
              {["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Aoû","Sep","Oct","Nov","Déc"].map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="ticker text-xs text-zinc-400">Jour</span>
            <input type="number" min="1" max="28" value={cfg.day} onChange={(e) => saveCfg({ day: parseInt(e.target.value) || 1 })} className="border border-zinc-800 px-2 py-1.5" data-testid="cfg-day"/>
          </label>
          <label className="flex flex-col gap-1">
            <span className="ticker text-xs text-zinc-400">Arrondi</span>
            <select value={cfg.rounding} onChange={(e) => saveCfg({ rounding: e.target.value })} className="border border-zinc-800 px-2 py-1.5" data-testid="cfg-rounding">
              <option value="none">Sans arrondi</option>
              <option value="1">1 $ près</option>
              <option value="5">5 $ près</option>
              <option value="10">10 $ près</option>
            </select>
          </label>
          <label className="flex items-center gap-2 col-span-2 text-sm">
            <input type="checkbox" checked={!!cfg.apply_to_existing_contracts} onChange={(e) => saveCfg({ apply_to_existing_contracts: e.target.checked })} data-testid="cfg-apply-contracts"/>
            Appliquer aussi aux contrats actifs des 86 élèves
          </label>
          <label className="flex items-center gap-2 col-span-2 text-sm">
            <input type="checkbox" checked={!!cfg.send_email_notification} onChange={(e) => saveCfg({ send_email_notification: e.target.checked })} data-testid="cfg-email"/>
            Envoyer email automatique aux membres
          </label>
          <div className="flex items-center text-xs text-zinc-400">
            Prochain : <strong className="ml-1">{cfg.next_run_at ? new Date(cfg.next_run_at).toLocaleDateString("fr-CA") : "—"}</strong>
          </div>
        </div>

        {/* Preview */}
        {preview && (
          <>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-zinc-100 font-display font-bold text-lg">Prévisualisation</h3>
              <div className="text-sm">
                <strong className="text-impact-gold">+{preview.total_revenue_delta.toFixed(2)} $</strong>
                <span className="text-zinc-400"> revenus mensuels supplémentaires estimés (sur {preview.count} tarifs)</span>
              </div>
            </div>
            <div className="border border-zinc-800 max-h-[280px] overflow-y-auto mb-4">
              <table className="w-full text-sm">
                <thead className="bg-foreground text-white sticky top-0">
                  <tr><th className="px-3 py-2 text-left">Tarif</th><th className="px-3 py-2 text-right">Ancien</th><th className="px-3 py-2 text-right">Nouveau</th><th className="px-3 py-2 text-right">Δ</th></tr>
                </thead>
                <tbody>
                  {preview.items.map(it => (
                    <tr key={it.id} className="border-b hover:bg-zinc-700/30">
                      <td className="px-3 py-1.5">{it.label}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{it.old_price.toFixed(2)} $</td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold">{it.new_price.toFixed(2)} $</td>
                      <td className={`px-3 py-1.5 text-right font-mono ${it.delta > 0 ? "text-emerald-700" : "text-zinc-400"}`}>{it.delta > 0 ? `+${it.delta.toFixed(2)}` : it.delta.toFixed(2)} $</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 mb-4">
              <button onClick={applyNow} disabled={applying} data-testid="apply-increase-btn" className="bg-impact-red hover:bg-foreground text-white px-5 py-2.5 ticker disabled:opacity-50">
                {applying ? "Application…" : `⚡ Appliquer +${cfg.pct}% MAINTENANT`}
              </button>
              <span className="text-xs text-zinc-400 self-center">L'augmentation sera aussi automatiquement déclenchée le {new Date(cfg.next_run_at).toLocaleDateString("fr-CA")}.</span>
            </div>
          </>
        )}

        {/* History */}
        <h3 className="text-zinc-100 font-display font-bold text-lg mb-2">Historique</h3>
        {history.length === 0 ? (
          <p className="text-sm text-zinc-400 italic">Aucune augmentation appliquée encore.</p>
        ) : (
          <div className="border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-700"><tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2">+%</th><th className="px-3 py-2">Tarifs</th><th className="px-3 py-2">Contrats</th><th className="px-3 py-2">Action</th></tr></thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} className="border-b">
                    <td className="px-3 py-1.5">{new Date(h.applied_at).toLocaleString("fr-CA")}</td>
                    <td className="px-3 py-1.5 text-center">{h.pct}%</td>
                    <td className="px-3 py-1.5 text-center">{h.tariffs_changed}</td>
                    <td className="px-3 py-1.5 text-center">{h.contracts_updated}</td>
                    <td className="px-3 py-1.5 text-center">
                      {h.rolled_back_at ? <span className="text-xs text-zinc-400 italic">Annulé</span> : (
                        <button onClick={() => rollback(h)} title="Annuler" className="hover:text-impact-red"><ArrowsCounterClockwise size={16}/></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
